import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { TopBar } from '../components/layout/TopBar';
import { eventsAPI, exportAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
  Plus, Calendar, Clock, Users, DollarSign, Ticket, QrCode, CheckCircle,
  Eye, Edit2, Trash2, Send, Download, RefreshCw, Search, ExternalLink,
  Copy, BarChart3, ArrowRight, X, Share2, Loader2, TrendingUp,
  AlertCircle, Check, ChevronRight, Zap, Settings
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ─── Status config ──────────────────────────────────────────────────────────
const EVENT_STATUS = {
  draft:     { label: 'Borrador',  pill: 'bg-slate-100 text-slate-600',     dot: 'bg-slate-400' },
  published: { label: 'Publicado', pill: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
  cancelled: { label: 'Cancelado', pill: 'bg-red-100 text-red-600',         dot: 'bg-red-500' },
  ended:     { label: 'Terminado', pill: 'bg-blue-100 text-blue-700',       dot: 'bg-blue-500' },
};

const TICKET_TYPES_PRESET = [
  { type: 'general', name: 'General', price: 100, quantity: 50 },
  { type: 'vip',     name: 'VIP',     price: 350, quantity: 20 },
  { type: 'table',   name: 'Mesa',    price: 800, quantity: 10 },
];

// ─── Event Card ──────────────────────────────────────────────────────────────
function EventCard({ event, onEdit, onDelete, onPublish, onView }) {
  const st = EVENT_STATUS[event.status] || EVENT_STATUS.draft;
  const dateStr = event.date ? format(parseISO(event.date), "d MMM yyyy", { locale: es }) : '';
  const totalTickets = (event.ticket_types || []).reduce((s, t) => s + (t.quantity || 0), 0);
  const sold = event.total_tickets_sold || 0;
  const revenue = event.total_revenue || 0;
  const progress = totalTickets > 0 ? Math.min(100, (sold / totalTickets) * 100) : 0;

  return (
    <Card className="overflow-hidden border-0 shadow-sm hover:shadow-md transition-all duration-200 flex flex-col">
      {/* Cover image or placeholder */}
      <div className="relative h-40 bg-gradient-to-br from-slate-800 to-slate-900 overflow-hidden flex-shrink-0">
        {event.image_url
          ? <img src={event.image_url} alt={event.name} className="w-full h-full object-cover opacity-80" />
          : <div className="w-full h-full flex items-center justify-center">
              <Calendar className="h-12 w-12 text-white/20" />
            </div>
        }
        <div className="absolute top-3 left-3">
          <span className={cn("text-[11px] font-semibold px-2.5 py-1 rounded-full shadow-sm", st.pill)}>
            {st.label}
          </span>
        </div>
        {event.status === 'draft' && (
          <button onClick={() => onPublish(event.id)}
            className="absolute bottom-3 right-3 bg-orange-500 hover:bg-orange-600 text-white text-[11px] font-medium px-3 py-1.5 rounded-lg shadow transition-colors flex items-center gap-1">
            <Send className="h-3 w-3" /> Publicar
          </button>
        )}
      </div>

      <CardContent className="p-4 flex flex-col flex-1">
        <div className="flex-1">
          <h3 className="font-bold text-base leading-tight mb-1 line-clamp-2">{event.name}</h3>
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500 mb-3">
            {event.date && (
              <span className="flex items-center gap-1">
                <Calendar className="h-3.5 w-3.5 text-orange-400" /> {dateStr}
              </span>
            )}
            {event.start_time && (
              <span className="flex items-center gap-1">
                <Clock className="h-3.5 w-3.5 text-blue-400" /> {event.start_time}
              </span>
            )}
            {event.max_capacity && (
              <span className="flex items-center gap-1">
                <Users className="h-3.5 w-3.5 text-purple-400" /> {event.max_capacity} cap.
              </span>
            )}
          </div>

          {/* Progress bar */}
          {totalTickets > 0 && (
            <div className="mb-3">
              <div className="flex justify-between text-[11px] text-slate-500 mb-1">
                <span>{sold} vendidos</span>
                <span>{totalTickets} total</span>
              </div>
              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-orange-500 rounded-full transition-all"
                  style={{ width: `${progress}%` }} />
              </div>
            </div>
          )}

          {/* Revenue */}
          {revenue > 0 && (
            <div className="flex items-center gap-1 text-sm font-semibold text-emerald-600">
              <DollarSign className="h-4 w-4" />
              ${revenue.toLocaleString('es-MX')} recaudado
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2 mt-3 pt-3 border-t">
          <Button size="sm" variant="outline" className="flex-1 h-8 text-xs" onClick={() => onView(event)}>
            <Eye className="h-3.5 w-3.5 mr-1" /> Ver
          </Button>
          <Button size="sm" variant="outline" className="h-8 text-xs px-2.5" onClick={() => onEdit(event)}>
            <Edit2 className="h-3.5 w-3.5" />
          </Button>
          <Button size="sm" variant="outline" className="h-8 text-xs px-2.5 text-red-500 hover:text-red-600"
            onClick={() => onDelete(event.id)}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Event Form ──────────────────────────────────────────────────────────────
function EventForm({ event, onSave, onCancel }) {
  const defaultTickets = [{ type: 'general', name: 'General', price: 100, quantity: 50 }];
  const [form, setForm] = useState({
    name: '', description: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    start_time: '20:00', end_time: '23:00',
    max_capacity: 100, image_url: '',
    ticket_types: defaultTickets,
    show_remaining_tickets: true,
    allow_waitlist: false,
    ...event,
  });
  const [saving, setSaving] = useState(false);

  const setTicket = (i, field, value) => {
    setForm(f => {
      const tts = [...f.ticket_types];
      tts[i] = { ...tts[i], [field]: field === 'price' || field === 'quantity' ? +value || 0 : value };
      return { ...f, ticket_types: tts };
    });
  };

  const addTicket = () => {
    const preset = TICKET_TYPES_PRESET[form.ticket_types.length % TICKET_TYPES_PRESET.length];
    setForm(f => ({ ...f, ticket_types: [...f.ticket_types, { ...preset }] }));
  };

  const removeTicket = (i) => setForm(f => ({
    ...f, ticket_types: f.ticket_types.filter((_, idx) => idx !== i)
  }));

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('El nombre es obligatorio'); return; }
    setSaving(true);
    try {
      if (event?.id) {
        await eventsAPI.update(event.id, form);
        toast.success('Evento actualizado');
      } else {
        await eventsAPI.create(form);
        toast.success('Evento creado');
      }
      onSave();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error al guardar'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5">
      {/* Basic info */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-slate-700 border-b pb-1">Información general</h3>
        <div>
          <Label className="text-xs text-slate-500 mb-1 block">Nombre del evento *</Label>
          <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            placeholder="Cena de Año Nuevo 2025..." className="text-sm" />
        </div>
        <div>
          <Label className="text-xs text-slate-500 mb-1 block">Descripción</Label>
          <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            placeholder="Describe el evento..." rows={3} className="text-sm resize-none" />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Fecha</Label>
            <Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} className="text-sm" />
          </div>
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Hora inicio</Label>
            <Input type="time" value={form.start_time} onChange={e => setForm(f => ({ ...f, start_time: e.target.value }))} className="text-sm" />
          </div>
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Hora fin</Label>
            <Input type="time" value={form.end_time} onChange={e => setForm(f => ({ ...f, end_time: e.target.value }))} className="text-sm" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Capacidad máx.</Label>
            <Input type="number" min={1} value={form.max_capacity}
              onChange={e => setForm(f => ({ ...f, max_capacity: +e.target.value || 0 }))} className="text-sm" />
          </div>
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">URL imagen de portada</Label>
            <Input value={form.image_url || ''} onChange={e => setForm(f => ({ ...f, image_url: e.target.value }))}
              placeholder="https://..." className="text-sm" />
          </div>
        </div>
        <div className="flex items-center gap-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <Switch checked={form.show_remaining_tickets}
              onCheckedChange={v => setForm(f => ({ ...f, show_remaining_tickets: v }))} />
            <span className="text-xs text-slate-600">Mostrar disponibilidad</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <Switch checked={form.allow_waitlist}
              onCheckedChange={v => setForm(f => ({ ...f, allow_waitlist: v }))} />
            <span className="text-xs text-slate-600">Lista de espera</span>
          </label>
        </div>
      </div>

      {/* Ticket types */}
      <div className="space-y-3">
        <div className="flex items-center justify-between border-b pb-1">
          <h3 className="text-sm font-semibold text-slate-700">Tipos de ticket</h3>
          <Button size="sm" variant="outline" onClick={addTicket} className="h-7 text-xs">
            <Plus className="h-3.5 w-3.5 mr-1" /> Añadir tipo
          </Button>
        </div>
        {form.ticket_types.map((tt, i) => (
          <div key={i} className="flex gap-2 items-end">
            <div className="flex-1">
              <Label className="text-[10px] text-slate-400 mb-0.5 block">Nombre</Label>
              <Input value={tt.name} onChange={e => setTicket(i, 'name', e.target.value)}
                placeholder="Tipo..." className="text-xs h-8" />
            </div>
            <div className="w-24">
              <Label className="text-[10px] text-slate-400 mb-0.5 block">Precio $</Label>
              <Input type="number" min={0} value={tt.price} onChange={e => setTicket(i, 'price', e.target.value)}
                className="text-xs h-8" />
            </div>
            <div className="w-24">
              <Label className="text-[10px] text-slate-400 mb-0.5 block">Cantidad</Label>
              <Input type="number" min={1} value={tt.quantity} onChange={e => setTicket(i, 'quantity', e.target.value)}
                className="text-xs h-8" />
            </div>
            <Button size="sm" variant="ghost" onClick={() => removeTicket(i)}
              className="h-8 w-8 p-0 text-red-400 hover:text-red-600">
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        ))}
      </div>

      <div className="flex gap-2 pt-2 border-t">
        <Button variant="outline" className="flex-1" onClick={onCancel}>Cancelar</Button>
        <Button className="flex-1 bg-orange-500 hover:bg-orange-600 text-white" onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
          {event?.id ? 'Guardar cambios' : 'Crear evento'}
        </Button>
      </div>
    </div>
  );
}

// ─── Event Detail Drawer ─────────────────────────────────────────────────────
function EventDetail({ event, onClose, onRefresh }) {
  const [tab, setTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [ticketSearch, setTicketSearch] = useState('');
  const [qrInput, setQrInput] = useState('');
  const [checkResult, setCheckResult] = useState(null);
  const [checking, setChecking] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [sr, tr] = await Promise.all([
          eventsAPI.getStats(event.id),
          eventsAPI.getTickets(event.id),
        ]);
        setStats(sr.data);
        setTickets(tr.data || []);
      } catch { toast.error('Error al cargar detalles'); }
      finally { setLoading(false); }
    };
    load();
  }, [event.id]);

  const filteredTickets = useMemo(() => {
    if (!ticketSearch) return tickets;
    const q = ticketSearch.toLowerCase();
    return tickets.filter(t =>
      t.buyer_name?.toLowerCase().includes(q) ||
      t.buyer_email?.toLowerCase().includes(q) ||
      t.qr_code?.toLowerCase().includes(q)
    );
  }, [tickets, ticketSearch]);

  const handleCheckIn = async () => {
    if (!qrInput.trim()) return;
    setChecking(true);
    setCheckResult(null);
    try {
      const res = await eventsAPI.checkIn(qrInput.trim());
      setCheckResult({ ok: true, ticket: res.data });
      toast.success('✅ Check-in exitoso');
      setQrInput('');
    } catch (e) {
      setCheckResult({ ok: false, msg: e?.response?.data?.detail || 'Ticket no encontrado' });
    } finally { setChecking(false); }
  };

  const handleExport = async () => {
    try {
      const res = await exportAPI.eventTickets(event.id, 'csv');
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `tickets-${event.id}.csv`; a.click();
    } catch { toast.error('Error al exportar'); }
  };

  const shareUrl = `${API_URL?.replace('/api', '')}/evento/${event.slug || event.id}`;

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <div className="w-full max-w-2xl bg-white shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-5 py-4 border-b flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className={cn("text-[11px] font-semibold px-2 py-0.5 rounded-full",
                EVENT_STATUS[event.status]?.pill)}>
                {EVENT_STATUS[event.status]?.label}
              </span>
            </div>
            <h2 className="font-bold text-lg leading-tight truncate">{event.name}</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {event.date && format(parseISO(event.date), "EEEE d 'de' MMMM yyyy", { locale: es })}
              {event.start_time && ` · ${event.start_time}`}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 flex-shrink-0">
            <X className="h-5 w-5 text-slate-400" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b px-5">
          <div className="flex gap-1">
            {[
              { k: 'overview', l: 'Resumen' },
              { k: 'tickets',  l: 'Tickets' },
              { k: 'checkin',  l: 'Check-in' },
            ].map(t => (
              <button key={t.k} onClick={() => setTab(t.k)}
                className={cn("px-4 py-2.5 text-sm font-medium border-b-2 transition-colors",
                  tab === t.k ? "border-orange-500 text-orange-600" : "border-transparent text-slate-500 hover:text-slate-700")}>
                {t.l}
              </button>
            ))}
            <div className="ml-auto flex items-center gap-2 py-2">
              <button onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success('Enlace copiado'); }}
                className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 px-2 py-1 rounded hover:bg-slate-100">
                <Copy className="h-3.5 w-3.5" /> Copiar enlace
              </button>
              <button onClick={() => navigate('/event-widget')}
                className="flex items-center gap-1 text-xs text-orange-500 hover:text-orange-600 px-2 py-1 rounded hover:bg-orange-50">
                <Settings className="h-3.5 w-3.5" /> Widget
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
            </div>
          ) : (
            <>
              {tab === 'overview' && stats && (
                <div className="p-5 space-y-4">
                  {/* KPI grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                      { label: 'Tickets vendidos', value: stats.total_sold || 0, icon: Ticket, color: 'text-orange-500' },
                      { label: 'Check-ins',         value: stats.checked_in || 0, icon: CheckCircle, color: 'text-emerald-500' },
                      { label: 'Recaudado',          value: `$${(stats.total_revenue || 0).toLocaleString()}`, icon: DollarSign, color: 'text-blue-500' },
                      { label: 'Disponibles',        value: (event.max_capacity || 0) - (stats.total_sold || 0), icon: Users, color: 'text-purple-500' },
                    ].map(k => (
                      <div key={k.label} className="rounded-xl border p-3 text-center">
                        <k.icon className={cn("h-5 w-5 mx-auto mb-1", k.color)} />
                        <div className="text-xl font-bold">{k.value}</div>
                        <div className="text-[11px] text-slate-500">{k.label}</div>
                      </div>
                    ))}
                  </div>

                  {/* By ticket type */}
                  {stats.by_ticket_type && Object.keys(stats.by_ticket_type).length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-slate-700 mb-2">Por tipo de ticket</h4>
                      <div className="space-y-2">
                        {Object.entries(stats.by_ticket_type).map(([type, data]) => (
                          <div key={type} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                            <div>
                              <p className="text-sm font-medium capitalize">{type}</p>
                              <p className="text-xs text-slate-500">
                                {data.sold} vendidos · {data.checked_in} check-ins
                              </p>
                            </div>
                            <span className="font-semibold text-emerald-600">${(data.revenue || 0).toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Description */}
                  {event.description && (
                    <div>
                      <h4 className="text-sm font-semibold text-slate-700 mb-1">Descripción</h4>
                      <p className="text-sm text-slate-600 leading-relaxed">{event.description}</p>
                    </div>
                  )}
                </div>
              )}

              {tab === 'tickets' && (
                <div className="p-5 space-y-3">
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                      <Input className="pl-8 text-sm h-9" placeholder="Buscar por nombre, email o QR..."
                        value={ticketSearch} onChange={e => setTicketSearch(e.target.value)} />
                    </div>
                    <Button variant="outline" size="sm" onClick={handleExport} className="h-9">
                      <Download className="h-4 w-4 mr-1" /> CSV
                    </Button>
                  </div>

                  <div className="rounded-xl border overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 border-b">
                        <tr>
                          <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500">Comprador</th>
                          <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500">Tipo</th>
                          <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500 hidden sm:table-cell">QR</th>
                          <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500">Estado</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {filteredTickets.length === 0 ? (
                          <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-400 text-xs">Sin tickets</td></tr>
                        ) : filteredTickets.map(t => (
                          <tr key={t.id} className="hover:bg-slate-50">
                            <td className="px-4 py-3">
                              <p className="font-medium text-sm">{t.buyer_name}</p>
                              <p className="text-xs text-slate-400">{t.buyer_email}</p>
                            </td>
                            <td className="px-4 py-3 text-xs text-slate-600 capitalize">{t.ticket_type}</td>
                            <td className="px-4 py-3 text-[11px] text-slate-400 font-mono hidden sm:table-cell">
                              {t.qr_code?.slice(0, 12)}...
                            </td>
                            <td className="px-4 py-3">
                              <span className={cn("text-[11px] px-2 py-0.5 rounded-full font-medium",
                                t.status === 'checked_in'
                                  ? 'bg-emerald-100 text-emerald-700'
                                  : t.status === 'cancelled'
                                  ? 'bg-red-100 text-red-600'
                                  : 'bg-amber-100 text-amber-700')}>
                                {t.status === 'checked_in' ? 'Check-in' : t.status === 'cancelled' ? 'Cancelado' : 'Activo'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {tab === 'checkin' && (
                <div className="p-5 space-y-4">
                  <div className="rounded-xl border-2 border-dashed border-slate-200 p-6 text-center">
                    <QrCode className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-sm font-medium text-slate-700 mb-3">Ingresa el código QR del ticket</p>
                    <div className="flex gap-2 max-w-sm mx-auto">
                      <Input
                        className="text-sm" placeholder="Escanea o escribe el código..."
                        value={qrInput} onChange={e => setQrInput(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleCheckIn()}
                        autoFocus
                      />
                      <Button onClick={handleCheckIn} disabled={checking || !qrInput.trim()}
                        className="bg-orange-500 hover:bg-orange-600 text-white px-4">
                        {checking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>

                  {checkResult && (
                    <div className={cn("rounded-xl p-4 border",
                      checkResult.ok ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200")}>
                      {checkResult.ok ? (
                        <div className="flex items-start gap-3">
                          <CheckCircle className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="font-semibold text-emerald-800">Check-in exitoso</p>
                            <p className="text-sm text-emerald-700">{checkResult.ticket?.buyer_name}</p>
                            <p className="text-xs text-emerald-600 capitalize">{checkResult.ticket?.ticket_type}</p>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-start gap-3">
                          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="font-semibold text-red-700">Check-in fallido</p>
                            <p className="text-sm text-red-600">{checkResult.msg}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Recent check-ins */}
                  {tickets.filter(t => t.status === 'checked_in').length > 0 && (
                    <div>
                      <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                        Check-ins recientes ({tickets.filter(t => t.status === 'checked_in').length})
                      </h4>
                      <div className="space-y-1">
                        {tickets.filter(t => t.status === 'checked_in').slice(0, 8).map(t => (
                          <div key={t.id} className="flex items-center gap-2 px-3 py-2 bg-emerald-50 rounded-lg">
                            <CheckCircle className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" />
                            <span className="text-xs font-medium flex-1 truncate">{t.buyer_name}</span>
                            <span className="text-[10px] text-slate-400 capitalize">{t.ticket_type}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function Events() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [viewingEvent, setViewingEvent] = useState(null);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await eventsAPI.list();
      setEvents(Array.isArray(res.data) ? res.data : []);
    } catch { toast.error('Error al cargar eventos'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este evento y todos sus tickets?')) return;
    try {
      await eventsAPI.delete(id);
      setEvents(prev => prev.filter(e => e.id !== id));
      toast.success('Evento eliminado');
    } catch { toast.error('Error al eliminar'); }
  };

  const handlePublish = async (id) => {
    try {
      await eventsAPI.publish(id);
      setEvents(prev => prev.map(e => e.id === id ? { ...e, status: 'published' } : e));
      toast.success('Evento publicado');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error al publicar'); }
  };

  const filteredEvents = useMemo(() => {
    return events.filter(e => {
      const q = searchTerm.toLowerCase();
      const matchSearch = !q || e.name?.toLowerCase().includes(q);
      const matchStatus = statusFilter === 'all' || e.status === statusFilter;
      return matchSearch && matchStatus;
    });
  }, [events, searchTerm, statusFilter]);

  const stats = useMemo(() => ({
    total: events.length,
    published: events.filter(e => e.status === 'published').length,
    revenue: events.reduce((s, e) => s + (e.total_revenue || 0), 0),
    tickets: events.reduce((s, e) => s + (e.total_tickets_sold || 0), 0),
  }), [events]);

  return (
    <div className="min-h-screen flex flex-col bg-slate-50" data-testid="events-page">
      <TopBar
        title="Eventos"
        helpSection="events"
        actions={
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate('/event-widget')} className="h-8">
              <Settings className="h-3.5 w-3.5 mr-1" /> Widget
            </Button>
            <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white h-8"
              onClick={() => { setEditingEvent(null); setShowForm(true); }}>
              <Plus className="h-4 w-4 mr-1" /> Nuevo evento
            </Button>
          </div>
        }
      />

      {/* Stats bar */}
      <div className="bg-white border-b px-6 py-3">
        <div className="flex flex-wrap gap-6">
          {[
            { label: 'Eventos',   value: stats.total,                  icon: Calendar,  color: 'text-slate-700' },
            { label: 'Activos',   value: stats.published,              icon: Zap,       color: 'text-emerald-600' },
            { label: 'Tickets',   value: stats.tickets,                icon: Ticket,    color: 'text-orange-600' },
            { label: 'Recaudado', value: `$${stats.revenue.toLocaleString()}`, icon: TrendingUp, color: 'text-blue-600' },
          ].map(s => (
            <div key={s.label} className="flex items-center gap-2">
              <s.icon className={cn("h-4 w-4", s.color)} />
              <span className={cn("font-bold", s.color)}>{s.value}</span>
              <span className="text-xs text-slate-400">{s.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white border-b px-4 py-2 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <Input className="pl-8 h-8 text-sm" placeholder="Buscar evento..."
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-8 text-xs w-36"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            {Object.entries(EVENT_STATUS).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <button onClick={fetchEvents} className="p-1.5 rounded hover:bg-slate-100 ml-auto">
          <RefreshCw className="h-4 w-4 text-slate-400" />
        </button>
      </div>

      {/* Events grid */}
      <div className="flex-1 p-4 sm:p-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
          </div>
        ) : filteredEvents.length === 0 ? (
          <div className="text-center py-20">
            <Calendar className="h-14 w-14 text-slate-200 mx-auto mb-4" />
            <p className="font-medium text-slate-500">
              {searchTerm || statusFilter !== 'all' ? 'Sin resultados' : 'No hay eventos aún'}
            </p>
            {!searchTerm && statusFilter === 'all' && (
              <Button className="mt-4 bg-orange-500 hover:bg-orange-600 text-white"
                onClick={() => { setEditingEvent(null); setShowForm(true); }}>
                <Plus className="h-4 w-4 mr-1" /> Crear primer evento
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredEvents.map(event => (
              <EventCard
                key={event.id}
                event={event}
                onEdit={e => { setEditingEvent(e); setShowForm(true); }}
                onDelete={handleDelete}
                onPublish={handlePublish}
                onView={e => setViewingEvent(e)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={v => { if (!v) { setShowForm(false); setEditingEvent(null); }}}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-orange-500" />
              {editingEvent ? 'Editar evento' : 'Nuevo evento'}
            </DialogTitle>
          </DialogHeader>
          <EventForm
            event={editingEvent}
            onSave={() => { setShowForm(false); setEditingEvent(null); fetchEvents(); }}
            onCancel={() => { setShowForm(false); setEditingEvent(null); }}
          />
        </DialogContent>
      </Dialog>

      {/* Event Detail Panel */}
      {viewingEvent && (
        <EventDetail
          event={viewingEvent}
          onClose={() => setViewingEvent(null)}
          onRefresh={fetchEvents}
        />
      )}
    </div>
  );
}
